import { useNavigate, useParams } from "react-router-dom";
import { Dropdown } from "primereact/dropdown";
import { useAuthContext } from "../../../hooks/useAuthContext";
import trainingsAPI from "../../../services/trainings";
import {
  PERMISSION_SCHOOL_TRAININGS,
  PERMISSION_TRAINING_CLASSES,
  PERMISSION_TRAINING_EVENTS,
  PERMISSION_TRAINING_PAYMENTS,
  PERMISSION_TRAINING_SLOT_CHANGE_REQUESTS,
  PERMISSION_TRAINING_STUDENTS,
  PERMISSION_TRAINING_TEACHERS,
} from "../../../permissions";

// Same permissions as the matching cards on the secretariat home page.
export const SECRETARIAT_SECTIONS = [
  { label: "Feuilles de présence", value: "presences", permission: PERMISSION_SCHOOL_TRAININGS.LIST },
  { label: "Liste des élèves", value: "students", permission: PERMISSION_TRAINING_STUDENTS.LIST },
  { label: "Liste des enseignant(e)s", value: "teachers", permission: PERMISSION_TRAINING_TEACHERS.LIST },
  { label: "Les classes", value: "classes", permission: PERMISSION_TRAINING_CLASSES.LIST },
  { label: "Gestion de paiements", value: "payments", permission: PERMISSION_TRAINING_PAYMENTS.LIST },
  { label: "Changements de créneau", value: "slot-change-requests", permission: PERMISSION_TRAINING_SLOT_CHANGE_REQUESTS.LIST },
  { label: "Agenda", value: "agenda", permission: PERMISSION_TRAINING_EVENTS.LIST },
];

const statusLabels = {
  OPEN: "Ouvert",
  DRAFT: "Brouillon",
  SUSPENDED: "Suspendu",
  ARCHIVED: "Archivé",
};

const statusClassName = (status) => {
  switch (status) {
    case "OPEN":
      return "bg-green-100 text-green-800";
    case "DRAFT":
      return "bg-blue-100 text-blue-800";
    case "SUSPENDED":
      return "bg-red-100 text-red-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const StatusBadge = ({ status, className = "" }) => (
  <span className={`text-xs px-2 py-1 border-round font-semibold ${statusClassName(status)} ${className}`}>
    {statusLabels[status] || status}
  </span>
);

// Breadcrumb for secretariat training pages: "section ▾ / training ▾ [/ trailing]".
const SecretariatWorkspaceBar = ({ section, training, trailing }) => {
  const { schoolId, trainingId } = useParams();
  const navigate = useNavigate();
  const { auth, hasPermission } = useAuthContext();

  const trainingsQuery = trainingsAPI.useGetList(schoolId, auth?.access_token, 1, 100, "");
  const trainings = trainingsQuery?.data?.data || [];
  const currentTraining = trainings.find((item) => item.id === trainingId) || training;

  const sectionOptions = SECRETARIAT_SECTIONS.filter(
    (item) => item.value === section || hasPermission(item.permission)
  );

  const valueTemplate = (option, props) => {
    const value = option || currentTraining;
    if (!value) {
      return <span>{props.placeholder}</span>;
    }
    return (
      <div className="flex align-items-center">
        <span className="font-bold text-color mr-2">{value.name}</span>
        {value.status && <StatusBadge status={value.status} />}
      </div>
    );
  };

  const itemTemplate = (option) => (
    <div className="flex align-items-center">
      <span className="font-medium text-color">{option.name}</span>
      <StatusBadge status={option.status} className="ml-2" />
    </div>
  );

  return (
    <div className="w-full p-breadcrumb border-bottom-1 surface-border py-2 px-3 flex align-items-center flex-wrap gap-2">
      <div className="flex align-items-center gap-2">
        <i className="pi pi-th-large text-teal-600 text-sm"></i>
        <Dropdown
          value={section}
          options={sectionOptions}
          onChange={(event) => {
            if (event.value && event.value !== section) {
              navigate(`/school/${schoolId}/secretariat/${event.value}${trainingId ? `/${trainingId}` : ""}`);
            }
          }}
          className="dropdown-breadcrumb font-bold text-sm sm:text-base"
          style={{ width: "auto" }}
        />
      </div>
      {trainingId && (
      <>
      <span className="text-300 font-light text-base sm:text-lg">/</span>
      <div className="flex align-items-center gap-2">
        <i className="pi pi-bookmark text-indigo-500 text-sm"></i>
        <Dropdown
          value={trainingId}
          options={trainings}
          optionLabel="name"
          optionValue="id"
          onChange={(event) => {
            if (event.value && event.value !== trainingId) {
              navigate(`/school/${schoolId}/secretariat/${section}/${event.value}`);
            }
          }}
          placeholder={currentTraining?.name || "Sélectionner une formation"}
          className="dropdown-breadcrumb font-bold text-sm sm:text-base"
          style={{ width: "auto" }}
          itemTemplate={itemTemplate}
          valueTemplate={valueTemplate}
        />
      </div>
      </>
      )}
      {trailing && (
        <>
          <span className="text-300 font-light text-base sm:text-lg">/</span>
          <span className="text-900 font-semibold text-sm sm:text-base px-2">{trailing}</span>
        </>
      )}
    </div>
  );
};

export default SecretariatWorkspaceBar;
